import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { ApiRequestError, type ApiResponse } from './types';
import { dispatchToast } from '@/components/ui/GlobalToast';
import {
  clearAuthSession,
  clearAccessToken as clearAccessTokenStorage,
  getAccessToken,
  getRefreshToken,
  persistAuthSession,
  setAccessToken as setAccessTokenStorage,
} from '@/utils/authStorage';

interface RefreshAuthPayload {
  user: unknown;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  accessExpiresIn: string;
  refreshExpiresIn: string;
}

type RetriableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
};

let authRedirectScheduled = false;

function buildLoginTarget(reason?: string) {
  const { pathname, search, hash } = window.location;
  const redirect = pathname === '/login' ? '' : `${pathname}${search}${hash}`;
  const params = new URLSearchParams();
  if (reason) params.set('reason', reason);
  if (redirect) params.set('redirect', redirect);
  const query = params.toString();
  return query ? `/login?${query}` : '/login';
}

async function navigateToLogin(target: string) {
  const navigateFromWindow = window.REACT_APP_NAVIGATE;
  if (navigateFromWindow) {
    navigateFromWindow(target, { replace: true });
    return;
  }

  const routerModule = await import('@/router');
  const navigate = window.REACT_APP_NAVIGATE || await routerModule.navigatePromise;
  navigate(target, { replace: true });
}

function handleUnauthorized(message = '登录已过期，请重新登录', reason?: string) {
  if (authRedirectScheduled) return;
  authRedirectScheduled = true;

  const loginTarget = buildLoginTarget(reason);
  clearAuthSession();
  dispatchToast('error', message);
  window.setTimeout(() => {
    void navigateToLogin(loginTarget)
      .catch(() => {
        window.location.assign(loginTarget);
      })
      .finally(() => {
        authRedirectScheduled = false;
      });
  }, 1200);
}

const apiBaseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const apiTimeout = Number(import.meta.env.VITE_API_TIMEOUT || 15000);
let refreshPromise: Promise<string> | null = null;

const http = axios.create({
  baseURL: apiBaseURL,
  timeout: apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

function normalizeHttpError(error: AxiosError): ApiRequestError {
  const status = error.response?.status;
  const data = error.response?.data as ApiResponse | undefined;

  if (data && typeof data.code === 'number' && typeof data.message === 'string') {
    return new ApiRequestError({
      code: data.code,
      message: data.message,
      details: data.details,
      requestId: data.requestId,
      timestamp: data.timestamp,
      httpStatus: status,
    });
  }

  const fallbackMessage = {
    400: '请求参数错误',
    401: '登录状态失效，请重新登录',
    403: '没有操作权限',
    404: '接口不存在',
    429: '请求过于频繁，请稍后重试',
    500: '服务器内部错误',
    503: '服务暂时不可用',
  }[status || 0] || '网络异常，请稍后重试';

  return new ApiRequestError({
    code: status ? status * 100 : -1,
    message: fallbackMessage,
    details: error.message,
    httpStatus: status,
  });
}

function persistRefreshedSession(payload: RefreshAuthPayload) {
  persistAuthSession(payload);
}

async function requestRefreshToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new ApiRequestError({
      code: 40100,
      message: '缺少 refresh token',
      httpStatus: 401,
    });
  }

  const response = await axios.post<ApiResponse<RefreshAuthPayload>>(
    `${apiBaseURL}/auth/refresh`,
    { refreshToken },
    {
      timeout: apiTimeout,
      headers: { 'Content-Type': 'application/json' },
    },
  );

  const payload = response.data;
  if (!payload || payload.code !== 0 || !payload.data?.accessToken) {
    throw new ApiRequestError({
      code: payload?.code ?? 40100,
      message: payload?.message || '刷新登录状态失败',
      details: payload?.details,
      httpStatus: response.status,
    });
  }

  persistRefreshedSession(payload.data);
  return payload.data.accessToken;
}

function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    refreshPromise = requestRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    const payload = response.data as ApiResponse;
    if (!payload || typeof payload.code !== 'number') {
      throw new ApiRequestError({
        code: -1,
        message: '响应结构不符合约定',
        details: response.data,
        httpStatus: response.status,
      });
    }
    if (payload.code !== 0) {
      throw new ApiRequestError({
        code: payload.code,
        message: payload.message,
        details: payload.details,
        requestId: payload.requestId,
        timestamp: payload.timestamp,
        httpStatus: response.status,
      });
    }
    response.data = payload.data;
    return response;
  },
  (error: AxiosError) => {
    const originalRequest = (error.config || {}) as RetriableRequestConfig;
    const requestUrl = String(originalRequest.url || '');
    const responsePayload = error.response?.data as ApiResponse | undefined;
    const bizCode = typeof responsePayload?.code === 'number' ? responsePayload.code : null;
    const isAccountDisabled = error.response?.status === 403 && bizCode === 40310;
    const shouldTryRefresh =
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      !requestUrl.includes('/auth/login') &&
      !requestUrl.includes('/auth/register') &&
      !requestUrl.includes('/auth/refresh');

    if (shouldTryRefresh) {
      originalRequest._retry = true;
      return refreshAccessTokenOnce()
        .then((newAccessToken) => {
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${newAccessToken}`,
          };
          return http.request(originalRequest);
        })
        .catch(() => {
          handleUnauthorized('登录已过期，请重新登录', 'session_expired');
          return Promise.reject(normalizeHttpError(error));
        });
    }

    if (error.response?.status === 401) {
      handleUnauthorized('登录已过期，请重新登录', 'session_expired');
    } else if (isAccountDisabled) {
      handleUnauthorized('账号已被停用或封禁，请联系管理员', 'account_disabled');
    }
    return Promise.reject(normalizeHttpError(error));
  },
);

export function setAccessToken(token: string) {
  setAccessTokenStorage(token);
}

export function clearAccessToken() {
  clearAccessTokenStorage();
}

export async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<T>(config);
  return response.data as T;
}

export function get<T = unknown>(url: string, config?: AxiosRequestConfig) {
  return request<T>({ ...config, method: 'GET', url });
}

export function post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return request<T>({ ...config, method: 'POST', url, data });
}

export function put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return request<T>({ ...config, method: 'PUT', url, data });
}

export function patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return request<T>({ ...config, method: 'PATCH', url, data });
}

export function del<T = unknown>(url: string, config?: AxiosRequestConfig) {
  return request<T>({ ...config, method: 'DELETE', url });
}
